<script setup lang="ts">
import { useDataFormatter } from "@/composable/data-formatter";
import { mdiDownloadOutline } from "@mdi/js";
import { ArchiveTask, ArchiveTaskState, useApi } from "@soft-db/monitoring-api-client";
import { Button, Icon, Progress, Tooltip } from "@soft-db/ui";
import { DateTime } from "luxon";
import { useSite } from "../../api/site-query";

const props = defineProps<{
	siteId: string;
	task: ArchiveTask;
}>();

const { t } = useI18n();
const { secondsToPrettyString } = useDataFormatter();

const progress = ref(props.task.progress);

const isReady = computed(() => progress.value?.finished || false);
const bundleAvailable = computed(() => progress.value?.bundleAvailable || false);

const { site } = useSite(props.siteId);

const archiveName = computed(() => {
	if (!site.value) return "";
	return `${site.value.name}-${DateTime.fromISO(props.task.startTime).toLocaleString(
		DateTime.DATE_FULL
	)}.mweb`
		.replaceAll(",", " ")
		.replaceAll(" ", "_");
});

const api = useApi().archive();

const { pause, resume } = useIntervalFn(async () => {
	const result = await api.getTaskProgress(props.siteId, props.task.id);

	progress.value = result.progress;
}, 2_000);

watchEffect(() => {
	if (progress.value?.finished) {
		pause();
	} else {
		resume();
	}
});

function getTaskDisplayName(task: ArchiveTaskState) {
	switch (task) {
		case "PACK_RECORDS":
		case "PACK_COMMENTS":
		case "PACK_EVENTS":
		case "PACK_CONFIGS":
			return t("archive.preparingArchive");

		case "DISCOVER_EVENTS_FILES":
			return t("archive.discoveringEvents");

		case "BUNDLE":
		case "VALIDATE":
			return t("archive.compressingAndValidate");
	}
}

function formatPercent(value: number | undefined) {
	if (!value) return 0;
	return Math.round(value * 100);
}

const taskStartIn = computed(() => {
	const { task } = props;
	if (task.progress) return "";
	if (!task.job) return "";

	const startIn = DateTime.fromISO(task.job.scheduledTime).diff(DateTime.now()).as("second");
	if (startIn < 5) return `${t("archive.startingTask")}...`;

	return t("archive.startingTaskIn", { n: secondsToPrettyString(startIn) });
});

async function download() {
	const { url } = await api.getTaskResult(props.siteId, props.task.id, archiveName.value);

	const downloadLink = document.createElement("a");
	downloadLink.href = url;
	downloadLink.download = "true";

	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);
}
</script>

<template>
	<template v-if="isReady">
		<Tooltip v-if="bundleAvailable" :tooltip="t('common.download')">
			<Button class="float-right" size="sm" variant="soft" color="primary" @click="download">
				<Icon :d="mdiDownloadOutline" />
			</Button>
		</Tooltip>
		<span v-else>{{ t("archive.noDataAvailable") }}</span>
	</template>

	<div v-else-if="progress != null">
		<Tooltip>
			<Progress class="progress-primary" :value="formatPercent(progress.progress)" />

			<template #tooltip>
				<div class="m-4 w-full whitespace-nowrap">
					<div>{{ getTaskDisplayName(progress.currentTask.type) }}</div>
					<div>{{ t("archive.progress") }}: {{ formatPercent(progress.progress) }}%</div>
				</div>
			</template>
		</Tooltip>
	</div>

	<div v-else>{{ taskStartIn }}</div>
</template>
